import { sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { fetchFile } from '@ffmpeg/util';
import { validateMedia, processFile, formatMediumBody } from './helper';
import * as api from './api';
import { uploadMedium } from './upload-medium';
import * as types from './types';
import { getStory } from '../stories/actions';
import { getStory as getStoryDetails, updateStory } from '../stories/api';
import { post } from 'utils/api';
import title from 'stories/title';

// Generates a JPEG thumbnail for a video file using the provided ffmpeg instance.
//
// Mirrors the server-side Ruby logic:
//   1. Run blackdetect on the first 60 s to find black areas.
//   2. If the video opens with a black section, seek past it before extracting.
//   3. Use ffmpeg's `thumbnail` filter (-vf thumbnail) to pick the most
//      representative frame rather than blindly grabbing a fixed timestamp.
//
// Returns { blob, filename } on success, throws on failure.
const generateVideoThumbnail = async (ffmpeg, file) => {
  const inputName = file.name;
  const thumbName = inputName.replace(/\.[^.]+$/, '') + '_thumb.jpg';

  await ffmpeg.writeFile(inputName, await fetchFile(file));

  // ── Step 1: detect black areas in the first 60 seconds ─────────────────────
  const blackAreas = [];
  const logHandler = ({ message }) => {
    // ffmpeg blackdetect emits lines like:
    //   [blackdetect @ 0x…] black_start:0 black_end:1.04167 black_duration:1.04167
    const match = message.match(/black_start:([\d.]+)\s+black_end:([\d.]+)/);
    if (match) {
      blackAreas.push({ start: parseFloat(match[1]), end: parseFloat(match[2]) });
    }
  };

  ffmpeg.on('log', logHandler);
  try {
    await ffmpeg.exec([
      '-t', '60', '-i', inputName,
      '-vf', 'blackdetect=d=0:pic_th=0.98:pix_th=0.1',
      '-an', '-f', 'null', '-',
    ]);
  } catch (_) {
    // blackdetect analysis exits with a non-zero code — that is expected; ignore.
  }
  ffmpeg.off('log', logHandler);

  // ── Step 2: if the video starts with black, seek past it ───────────────────
  // Matches the Ruby `split` path: when the first black area begins at ~0 s,
  // start thumbnail extraction from the end of that black section.
  let startTime = 0;
  if (blackAreas.length > 0 && blackAreas[0].start <= 0.1 && blackAreas[0].end != null) {
    startTime = blackAreas[0].end;
  }

  // ── Step 3: extract the most representative frame with the thumbnail filter ─
  // -vf thumbnail analyzes up to 100 frames and picks the best one, equivalent
  // to the Ruby `ffmpeg -vf "thumbnail" -frames:v 1` command.
  await ffmpeg.exec([
    '-ss', String(startTime),
    '-i', inputName,
    '-vf', 'thumbnail',
    '-frames:v', '1',
    thumbName,
  ]);

  const data = await ffmpeg.readFile(thumbName);
  const blob = new Blob([data.buffer], { type: 'image/jpeg' });
  return { blob, filename: thumbName };
};

// Reads the capture/recording date embedded in a file's metadata.
//
// For video files with ffmpeg available:
//   Probes the container for the `creation_time` tag (set by cameras/phones at
//   recording time). Falls back to file.lastModified if the tag is absent or
//   unparseable.
//
// For all other file types:
//   Returns file.lastModified, which most platforms set to the capture time
//   when transferring photos from a camera roll.
//
// Returns a Date object.
export const extractCreationDate = async (file, ffmpeg) => {
  if (ffmpeg && file.type.startsWith('video/')) {
    const inputName = `_probe_${file.name}`;
    let creationTime = null;

    const logHandler = ({ message }) => {
      // ffmpeg metadata output contains lines like:
      //   creation_time   : 2024-06-15T08:23:11.000000Z
      const match = message.match(/creation_time\s*:\s*(\S+)/i);
      if (match) creationTime = match[1];
    };

    try {
      await ffmpeg.writeFile(inputName, await fetchFile(file));
      ffmpeg.on('log', logHandler);
      // -i with no output just dumps metadata to the log then exits non-zero.
      await ffmpeg.exec(['-i', inputName]).catch(() => { });
      ffmpeg.off('log', logHandler);
      await ffmpeg.deleteFile(inputName).catch(() => { });
    } catch (_) {
      ffmpeg.off('log', logHandler);
    }

    if (creationTime) {
      const d = new Date(creationTime);
      if (!isNaN(d.getTime())) return d;
    }
  }

  return new Date(file.lastModified);
};

export const clearUploadBatch = () => ({ type: types.CLEAR_UPLOAD_BATCH });

export const createTextMedium = (storyId, values) => ({
  type: types.CREATE_TEXT_MEDIUM,
  promise: api.createStoryMedium(storyId, formatMediumBody(values)),
  meta: { storyId },
});

export const updateTextMedium = (id, values) => ({
  type: types.UPDATE_TEXT_MEDIUM,
  promise: api.updateTextMedium(values.id, formatMediumBody(values)),
});

export const cancelDestroyMediumPrompt = id => ({
  type: types.CANCEL_DESTROY_MEDIUM,
  id,
});

export const promptDestroyMedium = id => ({
  type: types.PROMPT_DESTROY_MEDIUM,
  id,
});

export const destroyMedium = (id, pageId, mediumType, storyId) => ({
  type: types.DESTROY_STORY_MEDIUM,
  promise: api.destroyStoryMedium(id),
  meta: {
    pageId,
    mediumType,
    storyId,
  },
});

const updateUploadProgress = (mediumId, progress) => ({
  type: types.UPDATE_UPLOAD_PROGRESS,
  mediumId,
  progress,
});

// Both of these now delegate to uploadMedium, which owns the transport, the retry
// policy, the progress guard and the whole `*_upload_*` event lifecycle. They stay
// as thin wrappers because a lot of call sites dispatch them, and because the
// upload-manager UI reads the redux batch that uploadMedium writes through them.
//
// Note the progress bug this removes. The version below computed
// `loaded * 100 / total` with no guard, so a progress event without a total gave
// NaN and the milestone comparisons all read false -- the batch path reported no
// progress whatsoever. The Watch Me Grow variant guarded it and the batch path did
// not, which is exactly the discrepancy the production telemetry showed.
export const uploadMediumFileForWatchMeGrow = async (_, medium, file, onProgress) => {
  const meta = {
    id: medium.id,
    mediumType: `${medium.type}s`.toLowerCase(), // convert 'Image' to 'images' e.g.
    fileSize: file.size,
    fileName: file.name,
  };

  const result = await uploadMedium({ medium, file, onProgress });

  // createMediaForWatchMeGrow branches on a rejection, so preserve that contract.
  if (!result.ok) throw result.error;
  return meta;
};

export const uploadMediumFile = (_, medium, file) => dispatch =>
  uploadMedium({ medium, file, dispatch });

export const createStoryMedium = (id, values, file = null) => dispatch => {
  const tmpUrl = URL.createObjectURL(file);
  dispatch({
    type: types.CREATE_STORY_MEDIUM,
    promise: api.createStoryMedium(id, formatMediumBody(values)),
    meta: {
      storyId: id,
      tmpUrl,
      onSuccess: (res, storyId) => {
        const { result, entities } = res.data;
        const mediumType = Object.keys(entities).filter(key => key !== 'pages')[0];
        const medium = entities[mediumType][result[mediumType][0]];
        dispatch(uploadMediumFile(storyId, medium, file));
      },
    },
  });
};

export const createStoryCoverMedium = (storyId, file) => dispatch => {
  const tmpUrl = URL.createObjectURL(file);
  dispatch({
    type: types.CREATE_STORY_MEDIUM_COVER,
    promise: api.createStoryCoverMedium(storyId, file.name),
    meta: {
      storyId,
      tmpUrl,
      onSuccess: res => {
        const { result, entities } = res.data;
        const mediumType = Object.keys(entities).filter(key => key !== 'pages')[0];
        const medium = entities[mediumType][result[mediumType][0]];
        dispatch(uploadMediumFile(storyId, medium, file));
        dispatch(getStory(storyId));
      },
    },
  });
};

export const createMedium = ({ attrs, tmpUrl }) => ({
  type: types.CREATE_MEDIUM,
  promise: api.postMedium({ attrs }),
  meta: { tmpUrl },
});

export const receiveStoryMedium = (storyId, payload, file) => {
  const tmpUrl = URL.createObjectURL(file);
  return {
    type: types.RECEIVE_STORY_MEDIUM,
    payload,
    storyId,
    tmpUrl,
  };
};

export const setRejectedMedia = names => ({
  type: types.SET_REJECTED_MEDIA,
  names,
});

export const createMediaForMemoirRequestFiles = (storyRequestId, files, onSubmit) => async dispatch => {
  if (!files.length) return false;
  const sortedFiles = sortBy(Array.from(files), file => file.lastModified);

  const { invalidMedia, validMedia } = validateMedia(sortedFiles);

  const mediaToCreate = await Promise.all(
    validMedia
      .map(async file => {
        const processedFile = await processFile(file);
        return processedFile;
      })
      .filter(file => !!file)
  );

  const mediaToUpload = await promiseChain(
    mediaToCreate.map((file, i) => async () => {
      const [values, fileBlob] = file;

      const payload = await api.postMedium({
        attrs: {
          ...values,
          ownerId: storyRequestId,
          ownerType: 'MemoirStoryRequest',
        },
      });

      const { result, entities } = payload.data;

      const mediumType = Object.keys(entities).filter(key => key !== 'pages')[0];
      const createdMedium = entities[mediumType][result[mediumType][0]];

      onSubmit({// this will patch the memoir_story_request object, setting ‘video_request_id’ = the video.id
        video_request_id: createdMedium?.id
      })
      return [createdMedium, fileBlob];
    })
  );

  if (invalidMedia.length) {
    dispatch(setRejectedMedia(invalidMedia.map(file => file.name)));
  }

  return mediaToUpload.forEach(([createdMedium, fileBlob]) => {
    dispatch(uploadMediumFile(storyRequestId, createdMedium, fileBlob));
  });
};

export const createMediaForWatchMeGrow = async (watch_me_grow_month_id, files, transcribe = false, otherAttributes, onUploadProgress) => {
  if (!files.length) return Promise.reject(new Error('No files provided'));

  const sortedFiles = sortBy(Array.from(files), file => file.lastModified);

  const { invalidMedia, validMedia } = validateMedia(sortedFiles);

  if (invalidMedia.length) {
    return Promise.reject(new Error(`Invalid media files: ${invalidMedia.map(file => file.name).join(', ')}`));
  }

  try {
    const mediaToCreate = await Promise.all(
      validMedia
        .map(async file => {
          const processedFile = await processFile(file);
          return processedFile;
        })
        .filter(file => !!file)
    );

    const mediaToUpload = await Promise.all(
      mediaToCreate.map(async (file) => {
        const [values, fileBlob] = file;
        const payload = await api.createWatchMeGrowMedium(
          watch_me_grow_month_id,
          formatMediumBody({
            ...values,
            ...(otherAttributes ? { ...otherAttributes } : {}),
            ...(transcribe ? { transcribe } : {})
          })
        );

        // Get created medium
        const { result, entities } = payload.data;
        const mediumType = Object.keys(entities).filter(key => key !== 'pages')[0];
        const createdMedium = entities[mediumType][result[mediumType][0]];

        return [createdMedium, fileBlob];
      })
    );

    const uploadResults = await Promise.all(
      mediaToUpload.map(([createdMedium, fileBlob]) =>
        uploadMediumFileForWatchMeGrow(watch_me_grow_month_id, createdMedium, fileBlob, onUploadProgress)
          .then(() => ({ success: true, createdMedium }))
          .catch((error) => ({ success: false, error }))
      )
    );

    return Promise.resolve(uploadResults); // Resolve with success data
  } catch (error) {
    return Promise.reject(error); // Reject with error
  }
};

// ─── ffmpeg serial queue ──────────────────────────────────────────────────────
// ffmpeg.wasm exposes a single-threaded WASM instance. Launching multiple
// ffmpeg.exec() calls concurrently corrupts its internal state and causes
// random failures or hangs. This lightweight queue ensures all background
// ffmpeg work (date extraction + thumbnail generation) runs one job at a time
// while never blocking the main upload path.
const makeFfmpegQueue = () => {
  let tail = Promise.resolve();
  return fn => {
    tail = tail.then(() => fn()).catch(() => { });
    return tail;
  };
};

// One queue per ffmpeg instance so concurrent uploads from different components
// don't interfere with each other.
const ffmpegQueues = new WeakMap();
const getFfmpegQueue = ffmpeg => {
  if (!ffmpegQueues.has(ffmpeg)) ffmpegQueues.set(ffmpeg, makeFfmpegQueue());
  return ffmpegQueues.get(ffmpeg);
};

// ─── Background: update story began-on date ───────────────────────────────────
// Fetches story details and updates began-on if the oldest file is earlier than
// the current story start date. Runs entirely after uploads have started.
const runStoryDateUpdate = async (storyId, validMedia, ffmpeg) => {
  try {
    // Use file.lastModified for a fast non-blocking estimate; upgrade to an
    // ffmpeg creation_time probe later if ffmpeg is available (queued below).
    const fileDates = validMedia.map(file => new Date(file.lastModified));
    const oldestDate = fileDates.reduce((oldest, d) => d < oldest ? d : oldest);

    const storyDetails = await getStoryDetails(storyId);
    const storyDate = storyDetails?.data?.initial?.data?.attributes?.['began-on'];

    if (oldestDate && new Date(storyDate) > oldestDate) {
      await updateStory(storyId, {
        'lock-version': storyDetails?.data?.initial?.data?.attributes?.['lock-version'],
        'began-on': oldestDate,
      });
    }
  } catch (err) {
    console.error('Background: story date update failed', err);
  }
};

// ─── Background: generate + upload thumbnail for one video ───────────────────
// Queues the ffmpeg work so concurrent videos don't race on the single instance.
const runThumbnailForVideo = (ffmpeg, enqueue, createdMedium, fileBlob) => {
  enqueue(async () => {
    try {
      const { blob: thumbBlob, filename: thumbFilename } = await generateVideoThumbnail(ffmpeg, fileBlob);

      const payload = await post('/v2/media', {
        type: 'Image',
        data: {
          attributes: {
            type: 'Image',
            'owner-type': 'Video',
            'owner-id': createdMedium?.id,
            filename: thumbFilename,
          },
        },
      });


      const { initial } = payload.data;
      const previewUploadUrl = initial?.data?.attributes?.['upload-url'];
      if (previewUploadUrl) {
        // Still not awaited -- the patch should not wait on the thumbnail landing --
        // but routed through uploadMedium so it retries, reports, and cannot leave an
        // unhandled rejection. It used to be a bare axios.put with no catch, which
        // meant a failed thumbnail produced an orphan Image stuck in pending_upload
        // and no record of why.
        uploadMedium({
          url: previewUploadUrl,
          mediumId: initial?.data?.id,
          mediumType: 'Image',
          file: thumbBlob,
        });

        await api.patchMedium(createdMedium.id, {
          'lock-version': initial?.data?.attributes?.['lock-version'],
          'video_thumbnail_id': initial?.data?.id,
        });

      }
    } catch (err) {
      console.error('Background: thumbnail generation failed for', createdMedium?.id, err);
    }
  });
};

// Resolves { created, failed } and never rejects. Two callers in
// stories/new-story-popup/submit-handlers.js chain `.then(handleSuccess)` with no
// catch, so a rejection here used to skip their success path entirely as well as
// producing an unhandled rejection.
export const createMediaForFiles = (storyId, files, position = 0, lastOnthePage = false, transcribe = false, resolvePromise = undefined, ffmpeg = null) => async dispatch => {
  try {
    return await createMediaForFilesUnsafe(
      { storyId, files, position, lastOnthePage, transcribe, resolvePromise, ffmpeg },
      dispatch
    );
  } catch (error) {
    console.error('createMediaForFiles failed', error);
    return { created: 0, failed: files.length, error };
  }
};

const createMediaForFilesUnsafe = async (
  { storyId, files, position, lastOnthePage, transcribe, resolvePromise, ffmpeg },
  dispatch
) => {
  if (!files.length) return { created: 0, failed: 0 };
  const sortedFiles = sortBy(Array.from(files), file => file.lastModified);

  const { invalidMedia, validMedia } = validateMedia(sortedFiles);

  if (invalidMedia.length) {
    dispatch(setRejectedMedia(invalidMedia.map(file => file.name)));
  }

  // ── PHASE 1: CRITICAL PATH ────────────────────────────────────────────────
  // Everything here must be as fast as possible. No ffmpeg, no extra HTTP calls.

  // processFile is synchronous in practice (just resolves with metadata), so
  // Promise.all completes in a single microtask tick for typical file counts.
  const mediaToCreate = (
    await Promise.all(validMedia.map(file => processFile(file)))
  ).filter(Boolean);

  // Create the records in parallel AND start each upload the moment its own record
  // comes back.
  //
  // This used to `await Promise.all(...)` over every create and only then run a
  // `forEach` that dispatched the uploads. Two things followed from that, and
  // together they were the single largest source of stalled media in production.
  //
  // First, no PUT was issued until the LAST create resolved. These creates go
  // through requestQueue at concurrency 6, so a large selection spends a long time
  // in that gap: production timestamps for one session on 2026-07-28 show 182 files
  // created over 71 seconds, and not one of the 182 ever uploaded. The UI mean-
  // while had been showing "Uploading" since the first create landed, because
  // receiveStoryMedium marks each file `uploading` as it arrives. Anything that
  // ended the page during that window -- navigating away, or iOS Safari reclaiming
  // a tab holding N blobs -- took the entire batch with it.
  //
  // Second, Promise.all is all-or-nothing. One rejected create among 182 rejected
  // the aggregate, the forEach never ran, and NOTHING uploaded -- while every
  // record that had been created successfully sat in pending_upload forever. The
  // thunk had no catch either, so that surfaced as an unhandled rejection and the
  // two callers that chain `.then(handleSuccess)` never ran their success path.
  //
  // allSettled plus a per-file dispatch fixes both: each upload starts as early as
  // it possibly can, and one bad create costs exactly one file.
  const settled = await Promise.allSettled(
    mediaToCreate.map(async (file, i) => {
      const [values, fileBlob] = file;
      const payload = await api.createStoryMedium(
        storyId,
        formatMediumBody({
          ...values,
          ...(lastOnthePage ? { position } : { 'page-position': position + i }),
          ...(transcribe ? { transcribe } : {}),
        })
      );
      dispatch(receiveStoryMedium(storyId, payload, fileBlob));

      const { result, entities } = payload.data;
      // `find`, and then a guard. This derivation assumes the response carries
      // exactly one non-`pages` entity type; if that ever stops holding,
      // createdMedium came back undefined and `medium['upload-url']` threw from
      // inside the old forEach -- which killed the whole batch, not just this file.
      const mediumType = Object.keys(entities).find(key => key !== 'pages');
      const createdMedium = mediumType && entities[mediumType][result[mediumType][0]];
      if (!createdMedium) {
        throw new Error(
          `created medium missing from response for ${values.filename} ` +
          `(entity types: ${Object.keys(entities).join(', ')})`
        );
      }

      if (resolvePromise) resolvePromise(createdMedium);

      // The upload starts here, not after every other create has finished.
      dispatch(uploadMediumFile(storyId, createdMedium, fileBlob));

      return [createdMedium, fileBlob];
    })
  );

  const mediaToUpload = settled
    .filter(outcome => outcome.status === 'fulfilled')
    .map(outcome => outcome.value);

  const failedCreates = settled.filter(outcome => outcome.status === 'rejected');
  if (failedCreates.length) {
    // Not routed to the rejected-media modal: its copy names unsupported file types
    // and the 2GB limit, which is not why these failed. Surfacing them needs its own
    // message -- see docs/upload_reliability_for_design.md.
    console.error(
      `createMediaForFiles: ${failedCreates.length} of ${mediaToCreate.length} records could not be created`,
      failedCreates.map(outcome => outcome.reason)
    );
  }

  // ── PHASE 2: BACKGROUND TASKS ─────────────────────────────────────────────
  // These run after uploads have started and never block the upload path.
  // Promise.allSettled ensures one failure doesn't cancel the others.

  const videoUploads = mediaToUpload.filter(
    ([createdMedium]) => createdMedium.type === 'Video' && ffmpeg
  );

  const enqueue = ffmpeg ? getFfmpegQueue(ffmpeg) : null;

  Promise.allSettled([
    // 2a. Story date update — uses file.lastModified (fast) instead of ffmpeg probe
    runStoryDateUpdate(storyId, validMedia, ffmpeg),

    // 2b. Thumbnail generation — serialized through the ffmpeg queue so concurrent
    //     videos don't race on the single WASM instance.
    ...videoUploads.map(([createdMedium, fileBlob]) =>
      new Promise(resolve => {
        runThumbnailForVideo(ffmpeg, enqueue, createdMedium, fileBlob);

        resolve(); // resolve immediately; the queue handles internal ordering
      })
    ),
  ]);

  // Background tasks continue independently.
  return { created: mediaToUpload.length, failed: failedCreates.length };
};

export const createMediumWithFile = ({ ownerId, file, mediumType }) => async dispatch => {
  const [attributes, blob] = await processFile(file);
  const tmpUrl = URL.createObjectURL(blob);
  return dispatch({
    type: types.CREATE_MEDIUM,
    promise: api.postStoryMedium({ id: ownerId, attributes }),
    //   attrs: { ...values, ownerType, ownerId },
    // }),
    meta: {
      tmpUrl,
      onSuccess: ({ data }) => {
        const medium = Object.values(data.entities[mediumType])[0];
        dispatch(uploadMediumFile(null, medium, blob));
      },
    },
  });
};
